import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  FormLabel,
  FormHelperText,
  TextField,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br'; // para PT-BR
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';

import { createAdminPublication } from '../../services/actaAdminPublicationsService';
import type { AdminPublicationCreatePayload } from '../../services/actaAdminPublicationsService';

import { getAllPublicEntitiesSimple } from '../../services/publicEntityService';
import { PublicEntitySimpleResponse } from '../../types/publicEntity';
import { authApi } from '../../services/authApi';

import { getSystems } from '../../services/systemService';
import type { SystemResponse } from '../../types/system';

import FileUploader from './FileUploader';

const AdminPublicationForm: React.FC = () => {
  const navigate = useNavigate();

  // Lista de entidades e a "option" selecionada (react-select)
  const [entities, setEntities] = useState<PublicEntitySimpleResponse[]>([]);
  const [selectedEntityOption, setSelectedEntityOption] = useState<{ value: string; label: string } | null>(null);

  // Lista de usuários (da entidade) e a "option" selecionada
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedUserOption, setSelectedUserOption] = useState<{ value: string; label: string } | null>(null);

  // ID do sistema "Publisol Acta" (buscamos em getSystems())
  const [publisolActaSystemId, setPublisolActaSystemId] = useState<string | null>(null);

  // Checkboxes (onde publicar)
  const [isForDom, setIsForDom] = useState<boolean>(false);
  const [isForDou, setIsForDou] = useState<boolean>(false);
  const [isForJgc, setIsForJgc] = useState<boolean>(false);

  // Apenas UM campo de data/hora para setar created_at / updated_at / published_at
  const [publicationDateTime, setPublicationDateTime] = useState<Dayjs | null>(null);

  // Arquivos
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<any[]>([]);

  // Novos campos (opcionais) para forçar edição e ano romano
  const [forcedEditionNumber, setForcedEditionNumber] = useState<string>('');
  const [forcedYearRomanStartFrom, setForcedYearRomanStartFrom] = useState<string>('');

  const [loading, setLoading] = useState(false);

  // 1) Carregamos a lista de entidades na montagem
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllPublicEntitiesSimple();
        setEntities(data);
      } catch (error) {
        console.error('Erro ao buscar entidades:', error);
        swal('Erro', 'Não foi possível buscar as entidades.', 'error');
      }
    })();
  }, []);

  // 2) Buscamos a lista de sistemas e tentamos encontrar o "Publisol Acta"
  useEffect(() => {
    (async () => {
      try {
        const allSystems: SystemResponse[] = await getSystems();
        const publisolActa = allSystems.find((sys) => sys.name === 'Publisol Acta');
        if (publisolActa) {
          setPublisolActaSystemId(publisolActa.id);
        } else {
          console.warn('Não foi encontrado o sistema "Publisol Acta".');
        }
      } catch (err) {
        console.error('Erro ao buscar sistemas:', err);
      }
    })();
  }, []);

  // 3) Sempre que trocar a entidade OU tivermos o ID do Acta, buscar os usuários correspondentes
  useEffect(() => {
    if (!selectedEntityOption || !publisolActaSystemId) {
      // Se limpou a entidade ou ainda não temos o ID do sistema, limpamos lista de usuários
      setUserOptions([]);
      setSelectedUserOption(null);
      return;
    }

    (async () => {
      try {
        const response = await authApi.get('/users', {
          params: {
            entity_id: selectedEntityOption.value,
            system_id: publisolActaSystemId,
          },
        });
        const userList = response.data as any[];
        const mappedUsers = userList.map((u) => ({
          value: u.id,
          label: u.full_name,
        }));
        setUserOptions(mappedUsers);
        setSelectedUserOption(null); // resetar user selecionado
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
        swal('Erro', 'Não foi possível buscar usuários desta entidade.', 'error');
      }
    })();
  }, [selectedEntityOption, publisolActaSystemId]);

  // Funções do FileUploader
  const handleFileDrop = (accepted: File[], rejected: any[]) => {
    setAcceptedFiles((prev) => [...prev, ...accepted]);
    setRejectedFiles((prev) => [...prev, ...rejected]);
  };
  const handleRemoveFile = (file: File) => {
    setAcceptedFiles((prev) => prev.filter((f) => f !== file));
  };

  // Submeter
  const handleSubmit = async () => {
    if (!selectedEntityOption) {
      swal('Atenção', 'Selecione a Entidade antes de prosseguir.', 'info');
      return;
    }
    if (!selectedUserOption) {
      swal('Atenção', 'Selecione o usuário (Criado por).', 'info');
      return;
    }
    if (acceptedFiles.length === 0) {
      swal('Atenção', 'Nenhum arquivo selecionado.', 'info');
      return;
    }
    if (!publicationDateTime) {
      swal('Atenção', 'Selecione a data/hora de publicação.', 'info');
      return;
    }

    setLoading(true);
    try {
      const payload: AdminPublicationCreatePayload = {
        entity_id: selectedEntityOption.value,
        created_by: selectedUserOption.value,
        created_by_name: selectedUserOption.label,
        is_for_dom: isForDom,
        is_for_dou: isForDou,
        is_for_jgc: isForJgc,
        files: acceptedFiles,
      };

      const dateIso = publicationDateTime.toISOString();
      payload.created_at = dateIso;
      payload.updated_at = dateIso;
      payload.published_at = dateIso;

      // Se o user preencheu forçando edição ou ano romano, convertemos para number
      if (forcedEditionNumber.trim() !== '') {
        payload.forced_edition_number = parseInt(forcedEditionNumber, 10);
      }
      if (forcedYearRomanStartFrom.trim() !== '') {
        payload.forced_year_roman_start_from = parseInt(forcedYearRomanStartFrom, 10);
      }

      const result = await createAdminPublication(payload);
      console.log('Publicação Admin criada:', result);

      swal('Sucesso', 'Publicação criada com sucesso!', 'success');
      navigate('/publicacoes-admin');
    } catch (error) {
      console.error('Erro ao criar publicação admin:', error);
      swal('Erro', 'Falha ao criar publicação. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Nova Publicação (Admin)
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Seletor de Entidade */}
          <Grid item xs={12} sm={6}>
            <FormLabel sx={{ mb: 1 }}>Entidade</FormLabel>
            <Select
              placeholder="Selecione a entidade..."
              value={selectedEntityOption}
              onChange={(option) => setSelectedEntityOption(option as any)}
              options={entities.map((ent) => ({ value: ent.id, label: ent.name }))}
              isClearable
            />
            <FormHelperText>
              Selecione a Entidade para filtrar os usuários
            </FormHelperText>
          </Grid>

          {/* Seletor de Usuário */}
          <Grid item xs={12} sm={6}>
            <FormLabel sx={{ mb: 1 }}>Usuário (Criado por)</FormLabel>
            <Select
              placeholder="Selecione o usuário..."
              value={selectedUserOption}
              onChange={(option) => setSelectedUserOption(option as any)}
              options={userOptions}
              isClearable
              isDisabled={!selectedEntityOption || !publisolActaSystemId}
            />
            <FormHelperText>
              Usuários pertencentes à Entidade e com acesso ao "Publisol Acta"
            </FormHelperText>
          </Grid>

          {/* Onde publicar */}
          <Grid item xs={12}>
            <FormLabel component="legend">Publicar em:</FormLabel>
            <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isForDom}
                    onChange={(e) => setIsForDom(e.target.checked)}
                  />
                }
                label="Diário Oficial Municipal (DOM)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isForDou}
                    onChange={(e) => setIsForDou(e.target.checked)}
                  />
                }
                label="Diário Oficial da União (DOU)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isForJgc}
                    onChange={(e) => setIsForJgc(e.target.checked)}
                  />
                }
                label="Jornal de Grande Circulação (JGC)"
              />
            </Box>
          </Grid>

          {/* ÚNICO campo de data/hora */}
          <Grid item xs={12} sm={6}>
            <FormLabel sx={{ mb: 1 }}>Data e Hora da Publicação</FormLabel>
            <DateTimePicker
              label={null}
              value={publicationDateTime}
              onChange={(newValue: Dayjs | null) => setPublicationDateTime(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Definirá created_at, updated_at e published_at',
                },
              }}
            />
          </Grid>

          {/* Campos para forçar edição / ano romano */}
          <Grid item xs={12} sm={3}>
            <TextField
              label="Forçar nº Edição"
              type="number"
              fullWidth
              value={forcedEditionNumber}
              onChange={(e) => setForcedEditionNumber(e.target.value)}
              helperText="(Opcional)"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Forçar Ano Romano"
              type="number"
              fullWidth
              value={forcedYearRomanStartFrom}
              onChange={(e) => setForcedYearRomanStartFrom(e.target.value)}
              helperText="(Opcional)"
            />
          </Grid>

          {/* Upload de arquivos PDF */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Arquivos (PDF):
            </Typography>
            <FileUploader
              onDrop={handleFileDrop}
              acceptedFiles={acceptedFiles}
              rejectedFiles={rejectedFiles}
              handleClearAll={() => {
                setAcceptedFiles([]);
                setRejectedFiles([]);
              }}
              handleRemoveFile={handleRemoveFile}
              handleRemoveRejectedFile={(file: File) =>
                setRejectedFiles((prev) => prev.filter((fr) => fr.file !== file))
              }
            />
          </Grid>

          {/* Botão de Enviar */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Criar Publicação'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AdminPublicationForm;